import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Users, Settings, Lightbulb, UserPlus, BarChart as ChartBar } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { CustomFieldsSection } from '../components/project/CustomFieldsSection';
import { InterviewsList } from '../components/project/InterviewsList';
import { InsightsSection } from '../components/project/InsightsSection';
import { ProjectHeader } from '../components/project/ProjectHeader';
import { EditorsSection } from '../components/project/EditorsSection';
import { InterviewAnalytics } from '../components/project/InterviewAnalytics';
import { isProjectOwner } from '../lib/permissions';

interface Project {
  id: string;
  title: string;
  description: string;
  target_interviews: number;
  status: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'interviews' | 'custom-fields' | 'insights' | 'editors' | 'analytics'>('interviews');
  const [isOwner, setIsOwner] = useState(false);
  const [interviews, setInterviews] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      checkOwnership(id);
      if (activeTab === 'analytics') {
        fetchInterviews(id);
        fetchCustomFields(id);
      }
    }
  }, [id, activeTab]);

  async function checkOwnership(projectId: string) {
    const ownerStatus = await isProjectOwner(projectId);
    setIsOwner(ownerStatus);
  }

  async function fetchProject(projectId: string) {
    try {
      const { data, error } = await supabase
        .from('interview_projects')
        .select(`
          id,
          title,
          description,
          target_interviews,
          status:status_id (
            id,
            name
          ),
          created_at,
          updated_at
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;
      setProject(data);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInterviews(projectId: string) {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          id,
          customer:customer_id (
            id,
            full_name,
            email
          ),
          custom_field_values
        `)
        .eq('project_id', projectId);

      if (error) throw error;
      setInterviews(data || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
    }
  }

  async function fetchCustomFields(projectId: string) {
    try {
      const { data, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 animate-spin text-brand-orange">
          <svg className="w-full h-full" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4 mt-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || 'Project not found'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[
          { name: 'Projects', href: '/projects' },
          { name: project.title }
        ]}
      />
      
      <ProjectHeader project={project} onProjectUpdate={setProject} />

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('interviews')}
            className={`${
              activeTab === 'interviews'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
          >
            <Users className="w-5 h-5 mr-2" />
            Interviews
          </button>
          <button
            onClick={() => setActiveTab('custom-fields')}
            className={`${
              activeTab === 'custom-fields'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
          >
            <Settings className="w-5 h-5 mr-2" />
            Custom Fields
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`${
              activeTab === 'insights'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
          >
            <Lightbulb className="w-5 h-5 mr-2" />
            Insights
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`${
              activeTab === 'analytics'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
          >
            <ChartBar className="w-5 h-5 mr-2" />
            Analytics
          </button>
          {isOwner && (
            <button
              onClick={() => setActiveTab('editors')}
              className={`${
                activeTab === 'editors'
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Editors
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'interviews' && <InterviewsList projectId={project.id} />}
        {activeTab === 'custom-fields' && <CustomFieldsSection projectId={project.id} />}
        {activeTab === 'insights' && <InsightsSection projectId={project.id} />}
        {activeTab === 'analytics' && (
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
            <h2 className="text-base font-semibold leading-6 text-brand-teal mb-4">Custom Field Analytics</h2>
            <InterviewAnalytics 
              interviews={interviews}
              customFields={customFields}
            />
          </div>
        )}
        {activeTab === 'editors' && isOwner && <EditorsSection projectId={project.id} />}
      </div>
    </div>
  );
}